import { Button, Input, Select } from '../../components/ui';
import { SOCIAL_STATUS_LABELS, SOCIAL_STATUS_ORDER } from './queries';
import { FormatLegend } from './FormatDot';
import {
  EMPTY_SOCIAL_FILTERS,
  UNASSIGNED,
  isFiltered,
  pillarOptions,
  platformOptions,
} from './filters';
import type { SelectOption } from '../../components/ui';
import type { SocialFilters } from './filters';
import type { SocialPost } from './queries';

/**
 * The Social Studio filter bar.
 *
 * Unlike the List View toolbar, the dropdowns sit in the open rather than
 * behind a Filters button. Filtering is the primary way Angel navigates 105
 * posts, and a control you use on every visit does not belong one click away.
 * The visual language is otherwise the same: ui-input controls, hairline
 * borders, hidden labels carried as aria-label so the row stays one line.
 *
 * Options come from the loaded posts, not from a fixed list, because platforms
 * and pillars are freehand text. A filter for something nobody has typed would
 * only ever return nothing.
 */

interface Person {
  user_id: string;
  display_name: string | null;
  email: string | null;
}

const ANY = '';

export function SocialFilterBar({
  filters,
  posts,
  people,
  matched,
  onChange,
  fixedPlatform,
  format,
}: {
  filters: SocialFilters;
  /** Unfiltered posts: the options must not shrink as you narrow the list. */
  posts: SocialPost[];
  people: Person[];
  matched: number;
  onChange: (next: SocialFilters) => void;
  /**
   * A tab that IS one platform (the LinkedIn Calendar) names it here. The
   * Platform dropdown is replaced by a plain label, because a dropdown that
   * cannot change anything is a control that lies.
   */
  fixedPlatform?: string | undefined;
  /** The optional Format dropdown, over the free-text Type field. */
  format?: { value: string; options: string[]; onChange: (next: string) => void } | undefined;
}) {
  const platforms: SelectOption[] = [
    { value: ANY, label: 'All platforms' },
    ...platformOptions(posts),
  ];

  const statuses: SelectOption[] = [
    { value: ANY, label: 'All statuses' },
    ...SOCIAL_STATUS_ORDER.map((status) => ({
      value: status,
      label: SOCIAL_STATUS_LABELS[status],
    })),
  ];

  const pillars: SelectOption[] = [
    { value: ANY, label: 'All pillars' },
    ...pillarOptions(posts).map((pillar) => ({ value: pillar, label: pillar })),
  ];

  const assignees: SelectOption[] = [
    { value: ANY, label: 'Anyone' },
    { value: UNASSIGNED, label: 'Unassigned' },
    ...people.map((person) => ({
      value: person.user_id,
      label: person.display_name ?? person.email ?? 'Unknown',
    })),
  ];

  // On a fixed-platform tab, a platform picked on another tab is not this
  // tab's filter: it is overridden, so it must not light up Clear here.
  const active =
    isFiltered(fixedPlatform === undefined ? filters : { ...filters, platform: ANY }) ||
    (format !== undefined && format.value !== ANY);

  return (
    <div className="filter-toolbar">
      <div className="filter-toolbar-row">
        <div className="filter-toolbar-search">
          <Input
            label="Search posts"
            labelHidden
            type="search"
            placeholder="Search title, caption, notes..."
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
          />
        </div>

        {fixedPlatform === undefined ? (
          <Select
            label="Platform"
            labelHidden
            options={platforms}
            value={filters.platform}
            onChange={(event) => onChange({ ...filters, platform: event.target.value })}
          />
        ) : (
          <span className="social-filter-fixed">{fixedPlatform} only</span>
        )}
        <Select
          label="Status"
          labelHidden
          options={statuses}
          value={filters.status}
          onChange={(event) => onChange({ ...filters, status: event.target.value })}
        />
        <Select
          label="Pillar"
          labelHidden
          options={pillars}
          value={filters.pillar}
          onChange={(event) => onChange({ ...filters, pillar: event.target.value })}
        />
        <Select
          label="Assignee"
          labelHidden
          options={assignees}
          value={filters.assignee}
          onChange={(event) => onChange({ ...filters, assignee: event.target.value })}
        />
        {format === undefined ? null : (
          <Select
            label="Format"
            labelHidden
            options={[
              { value: ANY, label: 'All formats' },
              ...format.options.map((option) => ({ value: option, label: option })),
            ]}
            value={format.value}
            onChange={(event) => format.onChange(event.target.value)}
          />
        )}

        {active ? (
          <Button
            variant="ghost"
            onClick={() => {
              onChange(EMPTY_SOCIAL_FILTERS);
              format?.onChange(ANY);
            }}
          >
            Clear
          </Button>
        ) : null}
      </div>

      {/* The key to the pills' format dots, right under the "LinkedIn only"
          label, so the colours are never a guessing game. Only where the
          Format filter is, which is only the LinkedIn Calendar. */}
      {format === undefined ? null : <FormatLegend />}

      {active ? (
        <p className="filter-toolbar-summary">
          {matched} of {posts.length} posts
        </p>
      ) : null}
    </div>
  );
}
